import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import {
  Building2,
  MapPin,
  Calendar,
  Euro,
  Filter,
  Search,
  TrendingUp,
  CheckCircle,
  ArrowDown,
} from 'lucide-react';

const Marktplatz = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('Alle');
  const [searchTerm, setSearchTerm] = useState('');

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/listings`);
        setListings(Array.isArray(res.data) ? res.data : []);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const industries = useMemo(
    () => ['Alle', ...new Set(listings.map((row) => row.industry))],
    [listings]
  );

  const filteredListings = listings.filter((listing) => {
    const matchesIndustry =
      selectedIndustry === 'Alle' || listing.industry === selectedIndustry;
    const matchesSearch =
      listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.location.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesIndustry && matchesSearch;
  });

  const scrollToListings = () => {
    const el = document.getElementById('listing-section');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>GmbH kaufen | Marktplatz für Bestands-GmbHs | Firmbay</title>
        <meta name="description" content="Finden Sie Ihre passende Bestands-GmbH auf dem Firmbay Marktplatz. Vorratsgesellschaften und Mantelgesellschaften kaufen - schnell und sicher." />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-10 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Marktplatz für
              <span className="block mt-2 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Bestands-GmbHs
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Wähle deinen Weg: Kauf eine bestehende Gesellschaft oder biete deine GmbH zum Verkauf an.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                size="lg"
                variant="outline"
                onClick={scrollToListings}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-base px-8"
              >
                GmbH kaufen
                <ArrowDown className="ml-2 w-5 h-5" />
              </Button>

              <Link to="/seller-onboarding">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white shadow-lg hover:shadow-xl transition-all text-base px-8"
                >
                  GmbH verkaufen
                  <TrendingUp className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            {/* Slim Sell CTA */}
            <div className="mt-8 max-w-4xl mx-auto">
              <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-yellow-50 shadow-sm">
                <CardContent className="py-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-left">
                      <div className="text-sm font-semibold text-orange-700 mb-1">GmbH verkaufen</div>
                      <div className="text-gray-700 text-sm">
                        Diskret, schnell und strukturiert – wir begleiten dich bei der Vermittlung.
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-700">
                        {[
                          'Diskrete Vermittlung',
                          'Bewertung & Vorprüfung',
                          'Notarielle Abwicklung',
                        ].map((item) => (
                          <div key={item} className="flex items-start">
                            <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 mr-2 flex-shrink-0" />
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <Link to="/seller-onboarding">
                        <Button className="w-full md:w-auto bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white shadow-md">
                          Angebot einreichen
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Suche nach Standort oder Titel..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Industry Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-5 h-5 text-gray-600" />
              {industries.map(industry => (
                <Button
                  key={industry}
                  variant={selectedIndustry === industry ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedIndustry(industry)}
                  className={selectedIndustry === industry ? 'bg-blue-600 hover:bg-blue-700' : ''}
                >
                  {industry}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Listings Section */}
      <section id="listing-section" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <p className="text-gray-600">
              {loading ? 'Lade Angebote…' : `${filteredListings.length} ${filteredListings.length === 1 ? 'Gesellschaft' : 'Gesellschaften'} gefunden`}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredListings.map(listing => (
              <Card key={listing.id} className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all h-full flex flex-col">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge
                      variant={listing.status === 'Verfügbar' ? 'default' : 'secondary'}
                      className={listing.status === 'Verfügbar' ? 'bg-teal-500' : 'bg-gray-400'}
                    >
                      {listing.status}
                    </Badge>
                    <Badge variant="outline" className="border-blue-600 text-blue-600">
                      {listing.industry}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl">{listing.title}</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed min-h-[72px] [display:-webkit-box] [-webkit-line-clamp:3] [-webkit-box-orient:vertical] overflow-hidden">
                    {listing.description}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-700">
                      <MapPin className="w-4 h-4 mr-2 text-blue-600" />
                      {listing.location}
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                      Gegründet {listing.foundedYear}
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                      Stammkapital: {listing.stammkapital.toLocaleString('de-DE')} €
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4 mb-4 flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Besonderheiten:</h4>
                    <ul className="space-y-1 min-h-[96px]">
                      {listing.features.map((feature, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start">
                          <span className="text-teal-500 mr-2">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-auto">
                    <div>
                      <div className="text-sm text-gray-600">Kaufpreis</div>
                      <div className="text-2xl font-bold text-gray-900 flex items-center">
                        {listing.price.toLocaleString('de-DE')}
                        <Euro className="w-5 h-5 ml-1" />
                      </div>
                    </div>
                    <Link to="/kontakt">
                      <Button
                        className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white"
                        disabled={listing.status !== 'Verfügbar'}
                      >
                        Anfragen
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Vorteile einer Bestands-GmbH
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: 'Sofort einsatzbereit',
                description: 'Keine Wartezeit für Handelsregistereintrag – du kannst direkt starten.',
              },
              {
                title: 'Etablierte Historie',
                description: 'Profitiere von einer bestehenden Unternehmensgeschichte und Reputation.',
              },
              {
                title: 'Bessere Bonität',
                description: 'Etablierte Gesellschaften haben oft bessere Bonitätsbewertungen bei Banken.',
              },
              {
                title: 'Zeitersparnis',
                description: 'Spare 2–4 Wochen gegenüber einer Neugründung.',
              },
            ].map((benefit, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-blue-200 transition-all">
                <CardHeader>
                  <CardTitle className="text-lg">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent className="h-full flex flex-col">
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Interesse an einer Bestands-GmbH?
          </h2>
          <p className="text-xl text-blue-50 mb-10">
            Kontaktiere uns für weitere Informationen und eine persönliche Beratung.
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl text-base px-8">
              Jetzt beraten lassen
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Marktplatz;